import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LineChart, Sparkles, RefreshCw } from "lucide-react";
import { fetchYieldComparison, fetchYieldComparisonAI } from "@/lib/api";
import { SkeletonRows, SkeletonBar } from "@/components/cot/Skeleton";

const COL_LABELS = {
  current: "Current",
  prev_day: "Prev Day",
  prev_week: "Prev Week",
  d30: "30 Day",
  d90: "90 Day",
};
const ORDER = ["current", "prev_day", "prev_week", "d30", "d90"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
};

function Sparkline({ data }) {
  if (!data || data.length < 2) return <span className="text-zinc-700">—</span>;
  const w = 76, h = 26, pad = 2;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const step = (w - pad * 2) / (data.length - 1);
  const yOf = (v) => pad + (h - pad * 2) * (1 - (v - min) / range);
  const pts = data.map((v, i) => `${(pad + i * step).toFixed(1)},${yOf(v).toFixed(1)}`);
  const up = data[data.length - 1] >= data[0];
  const color = up ? "#22C55E" : "#EF4444";
  const last = pts[pts.length - 1].split(",");
  const baseY = yOf(data[0]).toFixed(1);
  return (
    <svg width={w} height={h} className="overflow-visible" aria-hidden>
      <line x1={pad} y1={baseY} x2={w - pad} y2={baseY} stroke="#52525b" strokeWidth="0.6" strokeDasharray="2 2" opacity="0.7" />
      <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="1.6" fill={color} />
    </svg>
  );
}

function Delta({ bps, pct }) {
  if (bps == null) return null;
  const cls = bps > 0 ? "text-pos" : bps < 0 ? "text-neg" : "text-zinc-500";
  const sign = bps > 0 ? "+" : "";
  return (
    <div className="mt-0.5 leading-none">
      <div className={`text-[11px] tabular-nums font-medium ${cls}`}>
        {sign}{bps} bp
      </div>
      {pct != null && (
        <div className="text-[11px] tabular-nums text-zinc-500 mt-0.5">
          {pct > 0 ? "+" : ""}{pct.toFixed(2)}%
        </div>
      )}
    </div>
  );
}

// heat tint scaled by |bps|, capped at 40bp
function heatStyle(bps) {
  if (bps == null || bps === 0) return undefined;
  const intensity = Math.min(Math.abs(bps) / 40, 1) * 0.22;
  const rgb = bps > 0 ? "var(--heat-pos-rgb)" : "239,68,68";
  return { backgroundColor: `rgba(${rgb},${intensity.toFixed(3)})` };
}

function ValueCell({ value, pct, bps }) {
  return (
    <td className="px-2 py-2 text-right align-top" style={heatStyle(bps)}>
      <div className="text-[12px] tabular-nums text-zinc-100 leading-none">
        {value == null ? "—" : value.toFixed(2)}
      </div>
      <Delta bps={bps} pct={pct} />
    </td>
  );
}

export default function YieldComparison() {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const q = useQuery({
    queryKey: ["yield-comparison"],
    queryFn: () => fetchYieldComparison(false),
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
  const d = q.data;

  // Row with the largest day-over-day (Prev Day → Current) bp swing.
  const topMover = (() => {
    if (!d?.rows) return null;
    let best = null;
    for (const r of d.rows) {
      const b = r.bps?.current;
      if (b == null || b === 0) continue;
      if (!best || Math.abs(b) > Math.abs(best.bps)) best = { type: r.type, bps: b };
    }
    return best;
  })();

  // AI read is fetched separately so the table paints immediately.
  const aiQ = useQuery({
    queryKey: ["yield-comparison-ai"],
    queryFn: fetchYieldComparisonAI,
    enabled: !!d && !d.ai_read,
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
  const aiRead = d?.ai_read || aiQ.data?.ai_read || "";

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const fresh = await fetchYieldComparison(true);
      qc.setQueryData(["yield-comparison"], fresh);
      const ai = await fetchYieldComparisonAI();
      qc.setQueryData(["yield-comparison-ai"], ai);
    } catch {
      /* ignore */
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="yield-comparison-card">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
          <LineChart size={11} strokeWidth={1.5} className="text-amber-term" /> US Yield Comparison
        </span>
        <div className="flex items-center gap-2.5">
          <span className="text-[11px] uppercase tracking-widest text-zinc-600">
            ref · DFII10 {d?.reference_date ? fmtDate(d.reference_date) : ""}
          </span>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="yield-comparison-refresh"
            title="Refresh yields"
            className="text-zinc-500 hover:text-amber-term transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {q.isLoading ? (
        <SkeletonRows rows={7} cols={7} />
      ) : q.isError ? (
        <div className="h-[180px] flex items-center justify-center text-neg text-[11px] uppercase tracking-widest">
          Yield data unavailable
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-[11px]" data-testid="yield-comparison-table">
              <thead>
                <tr className="border-b border-zinc-800 text-[11px] uppercase tracking-widest text-zinc-500">
                  <th className="px-2 py-2 text-left font-medium w-[14.28%]">Type</th>
                  <th className="px-2 py-2 text-center font-medium w-[14.28%]" data-testid="yc-head-trend">
                    <div className="text-amber-term">90D Trend</div>
                    <div className="text-zinc-600 normal-case tracking-normal">old → new</div>
                  </th>
                  {ORDER.map((key) => {
                    const col = d.columns.find((c) => c.key === key);
                    return (
                      <th key={key} className="px-2 py-2 text-right font-medium w-[14.28%]" data-testid={`yc-head-${key}`}>
                        <div className="text-amber-term">{COL_LABELS[key]}</div>
                        <div className="text-zinc-600 normal-case tracking-normal">{fmtDate(col?.date)}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {d.rows.map((row) => {
                  const isTop = topMover && row.type === topMover.type;
                  return (
                  <tr
                    key={row.type}
                    className={`border-b border-zinc-900/70 last:border-0 transition-colors ${
                      isTop ? "bg-amber-500/5" : "hover:bg-zinc-900/40"
                    }`}
                    style={isTop ? { boxShadow: "inset 3px 0 0 0 hsl(var(--primary))" } : undefined}
                    data-testid={`yc-row-${row.type.replace(/[^a-zA-Z0-9]/g, "")}`}
                  >
                    <td className="px-2 py-2 text-left align-top text-zinc-300 font-mono">
                      <div className="truncate">{row.type}</div>
                      {isTop && (
                        <span
                          data-testid="yc-top-mover"
                          title={`Biggest day-over-day move: ${topMover.bps > 0 ? "+" : ""}${topMover.bps} bp`}
                          className={`inline-flex items-center gap-0.5 mt-1 text-[7px] uppercase tracking-wider px-1 leading-[1.5] border ${
                            topMover.bps > 0 ? "text-pos border-emerald-500/50" : "text-neg border-red-500/50"
                          }`}
                        >
                          {topMover.bps > 0 ? "▲" : "▼"} Top Mover
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 align-middle" data-testid={`yc-spark-${row.type.replace(/[^a-zA-Z0-9]/g, "")}`}>
                      <div className="flex justify-center">
                        <Sparkline data={row.spark} />
                      </div>
                    </td>
                    {ORDER.map((key) => (
                      <ValueCell key={key} value={row.values[key]} pct={row.pct?.[key]} bps={row.bps?.[key]} />
                    ))}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {aiRead ? (
            <div className="px-3 py-2 border-t border-zinc-800 flex items-start gap-1.5" data-testid="yc-ai-read">
              <Sparkles size={11} strokeWidth={1.5} className="text-amber-term mt-0.5 shrink-0" />
              <p className="text-[11px] leading-relaxed text-zinc-300">{aiRead}</p>
            </div>
          ) : aiQ.isLoading || aiQ.isFetching ? (
            <div className="px-3 py-2 border-t border-zinc-800 flex items-center gap-1.5" data-testid="yc-ai-loading">
              <Sparkles size={11} strokeWidth={1.5} className="text-amber-term shrink-0 animate-pulse" />
              <SkeletonBar className="h-3 w-3/4" />
            </div>
          ) : null}
          <p className="px-3 py-2 text-[11px] leading-relaxed text-zinc-600 border-t border-zinc-900">
            {d.note} Source: FRED · ECB (yields in %).
          </p>
        </>
      )}
    </div>
  );
}
